import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  Circle,
  PlayCircle,
  Sparkles,
  FileText,
  Award,
} from "lucide-react";
import AITutorPanel from "@/components/AITutorPanel";
import QuizModal from "@/components/QuizModal";

export default function LessonPlayer() {
  const { courseId } = useParams();
  const { refresh } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrol, setEnrol] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [showTutor, setShowTutor] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [issuingCert, setIssuingCert] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/courses/${courseId}`);
      setCourse(data);
      const enrolList = await api.get("/enrollments/me");
      const found = enrolList.data.find((x) => x.course?.id === courseId);
      if (found) setEnrol(found.enrollment);
      // pick first incomplete lesson
      const firstLesson = data.modules?.[0]?.lessons?.[0];
      const completed = new Set(found?.enrollment?.completed_lessons || []);
      const allLessons = (data.modules || []).flatMap((m) => m.lessons);
      const next = allLessons.find((l) => !completed.has(l.id)) || firstLesson;
      if (next) setActiveLessonId(next.id);
    })();
  }, [courseId]);

  const activeLesson = useMemo(() => {
    if (!course || !activeLessonId) return null;
    for (const m of course.modules || []) {
      const l = m.lessons.find((x) => x.id === activeLessonId);
      if (l) return { lesson: l, module: m };
    }
    return null;
  }, [course, activeLessonId]);

  const completedSet = new Set(enrol?.completed_lessons || []);
  const totalLessons = useMemo(
    () => (course?.modules || []).reduce((s, m) => s + m.lessons.length, 0),
    [course]
  );

  const completeLesson = async () => {
    if (!activeLessonId) return;
    try {
      const { data } = await api.post(`/enrollments/${courseId}/lesson-complete`, {
        lesson_id: activeLessonId,
      });
      setEnrol({ ...enrol, ...data });
      toast.success("Lesson marked complete");

      // Show MWZ award toasts
      if (Array.isArray(data.mwz_awarded)) {
        for (const a of data.mwz_awarded) {
          if (a.reason === "module_complete") {
            toast.success(`+${a.amount} MWZ · Module complete: ${a.module_title}`, { duration: 4000 });
          } else if (a.reason === "course_complete") {
            toast.success(`+${a.amount} MWZ · Course complete: ${a.course_title}`, { duration: 5000 });
          }
        }
        if (data.mwz_awarded.length > 0) {
          refresh();
        }
      }

      // auto-advance
      const allLessons = (course.modules || []).flatMap((m) => m.lessons);
      const idx = allLessons.findIndex((l) => l.id === activeLessonId);
      const next = allLessons.slice(idx + 1).find((l) => !data.completed_lessons.includes(l.id));
      if (next) setActiveLessonId(next.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark complete");
    }
  };

  const issueCertificate = async () => {
    setIssuingCert(true);
    try {
      const { data } = await api.post(`/certificates/issue/${courseId}`);
      toast.success(`Certificate issued · ${data.cert_hash.slice(0, 14)}…`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to issue certificate");
    } finally {
      setIssuingCert(false);
    }
  };

  if (!course) {
    return (
      <div className="p-10 mono text-xs uppercase tracking-widest text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!enrol) {
    return (
      <div className="p-10">
        <h2 className="font-display text-2xl font-extrabold">Not enrolled</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You need to enrol in this course first.
        </p>
        <Link to="/app/student/catalog">
          <Button className="mt-4 rounded-none bg-foreground text-background hard-shadow">
            Browse Catalogue →
          </Button>
        </Link>
      </div>
    );
  }

  const progress = enrol.progress_pct || 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12">
      {/* Sidebar curriculum */}
      <aside className="border-r border-border bg-muted/40 lg:col-span-3" data-testid="lesson-sidebar">
        <div className="border-b border-border p-5">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {course.category} · {course.duration_months} mo
          </div>
          <Link to="/app/student/courses" className="block">
            <h2 className="font-display mt-1 text-lg font-extrabold leading-tight tracking-tight hover:underline">
              {course.title}
            </h2>
          </Link>
          <div className="mt-3">
            <Progress value={progress} className="h-2 rounded-none" />
            <div className="mono mt-1.5 flex justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
              <span>{progress.toFixed(0)}% complete</span>
              <span>{completedSet.size}/{totalLessons}</span>
            </div>
          </div>
        </div>
        <Accordion type="multiple" defaultValue={course.modules.map((m) => m.id)}>
          {course.modules.map((m, mi) => (
            <AccordionItem key={m.id} value={m.id} className="border-b border-border">
              <AccordionTrigger className="px-5 py-3 text-sm font-semibold hover:no-underline">
                <span className="text-left">
                  <span className="mono mr-2 text-[10px] text-muted-foreground">
                    M{mi + 1}
                  </span>
                  {m.title}
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-0 pb-0">
                <ul className="bg-background">
                  {m.lessons.map((l) => {
                    const done = completedSet.has(l.id);
                    const active = activeLessonId === l.id;
                    return (
                      <li key={l.id}>
                        <button
                          onClick={() => setActiveLessonId(l.id)}
                          className={`flex w-full items-center gap-2 border-l-2 px-5 py-2.5 text-left text-sm ${
                            active
                              ? "border-foreground bg-muted font-semibold"
                              : "border-transparent hover:bg-muted/50"
                          }`}
                          data-testid={`lesson-item-${l.id}`}
                        >
                          {done ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-accent" />
                          ) : (
                            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                          <span className="flex-1 truncate">{l.title}</span>
                          <span className="mono text-[10px] text-muted-foreground">
                            {l.duration_min}m
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Quizzes section */}
        {course.quizzes?.length > 0 && (
          <div className="border-b border-border p-5">
            <div className="mono mb-2 text-[10px] uppercase tracking-widest text-muted-foreground">
              Quizzes
            </div>
            <ul className="space-y-1">
              {course.quizzes.map((q) => {
                const best = enrol?.quiz_scores?.[q.id];
                return (
                  <li key={q.id}>
                    <button
                      onClick={() => {
                        setActiveQuiz(q);
                        setQuizOpen(true);
                      }}
                      className="flex w-full items-center justify-between px-2 py-2 text-left text-sm hover:bg-muted/50"
                      data-testid={`quiz-launch-${q.id}`}
                    >
                      <span className="truncate">{q.title}</span>
                      <span className="mono text-[10px]">
                        {best != null ? `${best}%` : "—"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Cert issue */}
        {progress >= 100 && (
          <div className="border-b border-border p-5">
            <Button
              onClick={issueCertificate}
              disabled={issuingCert}
              className="w-full rounded-none bg-accent text-accent-foreground hard-shadow hover-lift"
              data-testid="issue-cert-btn"
            >
              <Award className="mr-2 h-4 w-4" />
              {issuingCert ? "Anchoring on-chain…" : "Issue Certificate"}
            </Button>
          </div>
        )}
      </aside>

      {/* Main lesson */}
      <main className="lg:col-span-9" data-testid="lesson-main">
        {activeLesson ? (
          <div className="mx-auto max-w-4xl p-6 md:p-10">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {activeLesson.module.title}
            </div>
            <h1 className="font-display mt-1 text-3xl font-extrabold tracking-tighter sm:text-4xl">
              {activeLesson.lesson.title}
            </h1>

            {/* Video frame */}
            {activeLesson.lesson.video_url && (
              <div className="mt-6 aspect-video border-2 border-foreground bg-foreground">
                <iframe
                  key={activeLesson.lesson.id}
                  className="h-full w-full"
                  src={activeLesson.lesson.video_url}
                  title={activeLesson.lesson.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {/* Content */}
            <div className="mt-6 prose prose-sm max-w-none text-foreground/90">
              <p className="leading-relaxed">{activeLesson.lesson.content}</p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {!completedSet.has(activeLesson.lesson.id) ? (
                <Button
                  onClick={completeLesson}
                  className="rounded-none bg-foreground text-background hard-shadow hover-lift"
                  data-testid="mark-complete-btn"
                >
                  <PlayCircle className="mr-2 h-4 w-4" /> Mark as complete →
                </Button>
              ) : (
                <div className="mono inline-flex items-center gap-2 border border-accent bg-accent/10 px-3 py-2 text-[11px] uppercase tracking-widest text-accent-foreground">
                  <CheckCircle2 className="h-4 w-4 text-accent" />
                  <span className="text-accent">Lesson Completed</span>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => setShowTutor(true)}
                className="rounded-none border-foreground hard-shadow-sm hover-lift"
                data-testid="open-ai-tutor-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" /> Ask AI Tutor
              </Button>
              {activeLesson.lesson.pdf_url && (
                <a href={activeLesson.lesson.pdf_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-none border-foreground hard-shadow-sm">
                    <FileText className="mr-2 h-4 w-4" /> Download PDF
                  </Button>
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="p-10 mono text-xs uppercase tracking-widest text-muted-foreground">
            Select a lesson to begin.
          </div>
        )}
      </main>

      <AITutorPanel
        open={showTutor}
        onClose={() => setShowTutor(false)}
        courseContext={course.title}
      />

      <QuizModal
        open={quizOpen}
        quiz={activeQuiz}
        onClose={() => setQuizOpen(false)}
        onComplete={async () => {
          // refresh enrol
          const { data } = await api.get("/enrollments/me");
          const found = data.find((x) => x.course?.id === courseId);
          if (found) setEnrol(found.enrollment);
        }}
      />
    </div>
  );
}
